import type { FlexProps } from '@chakra-ui/react'
import { Box, Flex, Spacer, Text } from '@chakra-ui/react'
import type { ReactNode } from 'react'

import { SparkleIcon } from './icons'
import { mastheadInset } from './layout'

/**
 * The sidebar.
 *
 * A wordmark, whatever there is to navigate between, and at the foot the
 * account area. An anonymous visitor still gets none of the middle — no list of
 * briefs, no saved work, no vendors — so navigation stays chrome for doors that
 * are locked or do not exist until a buyer has something to move between. Now
 * that one can, the caller decides when that is.
 *
 * Both slots are passed in rather than imported, so the shell stays a layout
 * and does not acquire a dependency on any feature.
 */
interface SidebarProps extends FlexProps {
  /** What there is to navigate between. Nothing, while there is nothing. */
  nav?: ReactNode
  footer?: ReactNode
}

export function Sidebar({ nav, footer, ...rest }: SidebarProps) {
  return (
    <Flex
      as="aside"
      aria-label="Sidebar"
      direction="column"
      w="sidebar"
      flexShrink="0"
      bg="sidebar.bg"
      color="sidebar.fg"
      px="3.5"
      pt={mastheadInset}
      pb="4"
      minH="0"
      overflowY="auto"
      {...rest}
    >
      {/*
        No top padding of its own: the wordmark is the masthead, so where it
        starts is the inset and not the inset plus whatever this row felt like
        adding. `pb` is kept because the space below it is spacing between two
        things rather than a measurement anything else is aligned to.
      */}
      <Flex align="center" gap="2.5" px="2.5" pb="1">
        <SparkleIcon boxSize="5" />
        {/*
          The editorial serif, which is what the reference sets the wordmark in
          and the one place in the chrome it is used. `fonts.display` exists for
          exactly this: a name, set once, rather than the grotesk everything
          else is made of.
        */}
        <Text
          fontFamily="display"
          fontSize="xl"
          fontWeight="semibold"
          letterSpacing="display"
          lineHeight="display"
        >
          Scout
        </Text>
      </Flex>

      {nav !== undefined && (
        <Box mt="6" flexShrink="0">
          {nav}
        </Box>
      )}

      <Spacer minH="6" />

      {/* flexShrink so a short viewport scrolls the sidebar rather than
          squashing the card out of shape. */}
      <Box flexShrink="0">{footer}</Box>
    </Flex>
  )
}
